import { ChangeDetectionStrategy, Component, effect, input, output, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatButtonModule } from "@angular/material/button";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { CustomStrategyOptions, CustomStrategyField } from "../../../_types/bot.type";

@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    selector: "app-custom-strategy-options-edit",
    templateUrl: "./custom-strategy-options-edit.component.html",
    styleUrl: "./custom-strategy-options-edit.component.scss",
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatButtonModule,
        FontAwesomeModule,
    ],
})
export class CustomStrategyOptionsEditComponent {
    public readonly StrategyOptions = input.required<CustomStrategyOptions>();
    public readonly StrategyOptionsChange = output<CustomStrategyOptions>();
    public readonly IsValid = output<boolean>();
    public readonly IsConfigurable = input<boolean>(false);

    private readonly _options = signal<CustomStrategyOptions>({
        isDefault: false,
        type: 0,
        fields: [],
    });

    public readonly fieldTypes: string[] = ["number", "string", "boolean"];

    constructor() {
        effect(() => {
            const inputOptions: CustomStrategyOptions = this.StrategyOptions();
            this._options.set({
                ...inputOptions,
                fields: inputOptions.fields ?? [],
            });
            this.validateOptions();
        });
    }

    get options(): CustomStrategyOptions {
        return this._options();
    }

    public onOptionsChange(): void {
        this.StrategyOptionsChange.emit(this._options());
        this.validateOptions();
    }

    public validateOptions(): void {
        const isValid: boolean = this.isValidOptions();
        this.IsValid.emit(isValid);
    }

    private isValidOptions(): boolean {
        const opts: CustomStrategyOptions = this._options();

        if (!opts) {
            return false;
        }

        // Validate fields
        if (opts.fields && opts.fields.length > 0) {
            for (const field of opts.fields) {
                // Check field name is not empty
                if (!field.name || field.name.trim().length === 0) {
                    return false;
                }

                // Check field type is valid
                if (!field.type || !this.fieldTypes.includes(field.type)) {
                    return false;
                }

                // Check duplicate field names
                const duplicates: number = opts.fields.filter((f: CustomStrategyField) => f.name === field.name).length;
                if (duplicates > 1) {
                    return false;
                }

                // Validate field value based on type
                if (field.type === "number") {
                    const numValue: number = Number(field.value);
                    if (isNaN(numValue)) {
                        return false;
                    }
                }

                if (field.type === "boolean") {
                    const boolValue: string = field.value.toLowerCase();
                    if (boolValue !== "true" && boolValue !== "false") {
                        return false;
                    }
                }
            }
        }

        return true;
    }

    public addField(): void {
        const currentOptions: CustomStrategyOptions = this._options();
        const currentFields: CustomStrategyField[] = currentOptions.fields ?? [];

        const newField: CustomStrategyField = {
            name: "",
            value: "",
            type: "string",
        };

        this._options.update((opts: CustomStrategyOptions) => ({
            ...opts,
            fields: [...currentFields, newField],
        }));
        this.onOptionsChange();
    }

    public removeField(index: number): void {
        const currentOptions: CustomStrategyOptions = this._options();
        const currentFields: CustomStrategyField[] = currentOptions.fields ?? [];

        this._options.update((opts: CustomStrategyOptions) => ({
            ...opts,
            fields: currentFields.filter((_: CustomStrategyField, i: number) => i !== index),
        }));
        this.onOptionsChange();
    }

    public onFieldChange(): void {
        this.onOptionsChange();
    }

    public getFieldInputType(fieldType: string): string {
        if (fieldType === "number") {
            return "number";
        }
        return "text";
    }

    public isFieldBoolean(fieldType: string): boolean {
        return fieldType === "boolean";
    }
}
